/**
 * Colours for the manual drawing tools.
 *
 * One active colour applies to every newly drawn shape and text annotation. Existing drawings keep
 * the colour they were created with — changing the selector re-colours nothing already on the
 * chart, which is what makes it usable for marking up a chart in more than one colour.
 */

/**
 * Palette offered by the toolbar swatch.
 *
 * These are the colours the drawing tools already used individually (blue for vertical lines, red
 * for horizontal, green for trendlines, orange for rectangles, purple for text) plus black and
 * grey for annotation over a busy chart. Keeping the old defaults in the palette means a chart
 * drawn before the selector existed still looks consistent with one drawn after.
 */
export const DRAWING_PALETTE = [
  { value: '#1976d2', label: 'Blue' },
  { value: '#e53935', label: 'Red' },
  { value: '#388e3c', label: 'Green' },
  { value: '#ff9800', label: 'Orange' },
  { value: '#7b1fa2', label: 'Purple' },
  { value: '#00897b', label: 'Teal' },
  { value: '#212121', label: 'Black' },
  { value: '#757575', label: 'Grey' },
];

/** The colour drawing starts on, chosen to match the previous vertical-line default. */
export const DEFAULT_DRAWING_COLOR = '#1976d2';

/**
 * A translucent version of a colour, for rectangle fills.
 *
 * The rectangle needs a solid border and a see-through fill, so a single hex cannot serve both —
 * a filled rectangle at full opacity would hide the candles it is meant to frame.
 *
 * Accepts `#rgb`, `#rrggbb`, or an existing `rgb()/rgba()` string. Anything unrecognised is
 * returned unchanged rather than throwing: a drawing tool must not break the chart because a
 * colour string was in an unexpected format.
 */
export function withAlpha(color, alpha = 0.18) {
  if (typeof color !== 'string') return color;
  const c = color.trim();

  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(c);
  if (short) {
    const [r, g, b] = short.slice(1).map(h => parseInt(h + h, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(c);
  if (long) {
    const [r, g, b] = long.slice(1).map(h => parseInt(h, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const rgb = /^rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)/i.exec(c);
  if (rgb) {
    return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  }

  return color;
}
